import { useToast } from '@kk/ui/components/Notifications/Toast';
import {
  type MutationObserverOptions,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { leasingApi } from '@/api';

export type ConnectUserRequest = { username: string; password: string };
export type ConnectUserResponse = { success: boolean; userId: string };

export function useConnectUser(
  uuid: string | undefined,
  options?: MutationObserverOptions<
    ConnectUserResponse,
    Error,
    ConnectUserRequest
  >,
) {
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  const { showToast } = useToast();

  return useMutation<ConnectUserResponse, Error, ConnectUserRequest>({
    ...options,
    mutationKey: uuid ? ['connectUser', uuid] : ['connectUser'],
    mutationFn: async ({ username, password }) => {
      if (!username || !password || !uuid) {
        throw new TypeError('Missing username, password, or uuid');
      }

      await leasingApi.postApiV1AuthenticateConnectuser({
        //pUUID: uuid, // MANGLER
        pWSFNUser: username,
        pPassword: password,
      });

      return {
        success: true,
        userId: uuid,
      };
    },
    onSuccess: async (data, variables, context, meta) => {
      if (uuid) {
        queryClient.invalidateQueries({ queryKey: ['connectedUser', uuid] });
      }

      options?.onSuccess?.(data, variables, context, meta);

      showToast({
        type: 'success',
        headline: t('common.connectUser.onSuccess'),
        size: 'long',
        link: { href: 'toast' },
      });
    },
    onError: async (error, variables, context, meta) => {
      options?.onError?.(error, variables, context, meta);

      showToast({
        type: 'danger',
        headline: t('common.connectUser.onError'),
        size: 'long',
        link: { href: 'toast' },
      });
    },
  });
}

export function useGetUserNames() {
  return useQuery({
    queryKey: ['usernames'],
    queryFn: () => leasingApi.getApiV1AuthenticateGetUserName(),
  });
}
